import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';

const AdminRoute = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        // Check if user is logged in
        const { data: { session }, error: authError } = await supabase.auth.getSession();
        
        if (authError || !session) {
          console.log('User not authenticated, redirecting to login');
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        
        // Check if we're in development mode (localhost)
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const isDev = isLocalhost;
        
        // Only log environment info in development
        if (import.meta.env.MODE === 'development') {
          console.log('Environment check:', {
            mode: import.meta.env.MODE,
            isProd: import.meta.env.PROD,
            isDev: import.meta.env.DEV
          });
        }
        
        if (isDev) {
          // DEVELOPMENT ENVIRONMENT: Allow any authenticated user to access admin routes
          console.log('DEV MODE: Bypassing admin check for development');
          setIsAdmin(true);
          setLoading(false);
          return;
        }
        
        // PRODUCTION ENVIRONMENT: Check actual admin status
        console.log('PRODUCTION MODE: Checking real admin status');
        
        // Try using the RPC function to check admin status (avoids RLS recursion)
        try {
          const { data: rpcData, error: rpcError } = await supabase
            .rpc('check_admin_status', { user_id: session.user.id });
            
          if (rpcError) {
            console.error('Admin check RPC error:', rpcError);
            
            // Fallback to using execute_sql RPC
            try {
              const { data: sqlData, error: sqlError } = await supabase.rpc('execute_sql', { 
                sql_statement: `SELECT is_admin, user_type FROM app_users WHERE id = '${session.user.id}'`
              });
              
              if (sqlError) {
                console.error('SQL execution error:', sqlError);
                setIsAdmin(false);
              } else {
                // Check results from the SQL query
                console.log('SQL query result:', sqlData);
                
                if (sqlData && sqlData[0]) {
                  const hasAdminPrivileges = 
                    sqlData[0].is_admin === true && 
                    sqlData[0].user_type === 'staff';
                    
                  console.log('User admin status from SQL:', {
                    is_admin: sqlData[0].is_admin,
                    user_type: sqlData[0].user_type,
                    hasAdminPrivileges
                  });
                  
                  setIsAdmin(hasAdminPrivileges);
                } else {
                  setIsAdmin(false);
                }
              }
            } catch (sqlCheckError) {
              console.error('Error in SQL admin check:', sqlCheckError);
              setIsAdmin(false);
            }
          } else {
            console.log('Admin check RPC result:', rpcData);
            if (rpcData === true) {
              // Double-check with a direct query to enforce stricter (is_admin AND user_type='staff') rule
              const { data: userRow, error: rowErr } = await supabase
                .from('app_users')
                .select('is_admin, user_type')
                .eq('id', session.user.id)
                .single();

              if (rowErr || !userRow) {
                console.error('Error verifying admin row:', rowErr);
                setIsAdmin(false);
              } else {
                const verified = userRow.is_admin === true && userRow.user_type === 'staff';
                setIsAdmin(verified);
              }
            } else {
              setIsAdmin(false);
            }
          }
        } catch (error) {
          console.error('Admin check error:', error);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Error in admin check:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, []);

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Checking permissions...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

export default AdminRoute; 